import { AxiosInstance, AxiosError, AxiosResponseHeaders, RawAxiosResponseHeaders } from 'axios'

import { AxiosErrorResponseData } from '@marketplace-web/core-api/api-client-util'
import { logMessage } from '@marketplace-web/observability/logging-util'

import { DD_RESPONSE_DISPLAYED_EVENT } from '../constants/events'
import { isAxiosErrorBlockedByDataDome } from '../utils/utils'
import {
  incrementDataDomeRequestBlocked,
  incrementDataDomeScriptMissing,
} from '../utils/observability'

const SCRIPT_FAILS_TO_TRIGGER_TIMEOUT = 10000

const handleDataDomeCaptchaFailedToRender = ({
  eventAbortController,
  headers,
  url,
}: {
  eventAbortController: AbortController
  headers: RawAxiosResponseHeaders | AxiosResponseHeaders | undefined
  url: string | undefined
}) => {
  incrementDataDomeScriptMissing()

  logMessage('DataDome captcha failed to render', {
    feature: 'data-dome-script-detection',
    extra: JSON.stringify({
      windowProperties: {
        ddoptions: 'ddoptions' in window ? window.ddoptions : undefined,
        dataDomeOptions: 'dataDomeOptions' in window ? window.dataDomeOptions : undefined,
      },
      blockedRequestId: headers?.['x-request-id'],
      blockedDataDomeId: headers?.['x-datadome-cid'],
      blockedUrl: url,
    }),
  })

  eventAbortController.abort()
}

const handleDataDomeCaptchaRendered = (timeoutId: NodeJS.Timeout) => {
  clearTimeout(timeoutId)
}

export const missingDataDomeScriptDetectionInterceptor = (instance: AxiosInstance) => {
  instance.interceptors.response.use(
    undefined,
    (error: AxiosError<AxiosErrorResponseData<unknown>>) => {
      if (
        error.response &&
        isAxiosErrorBlockedByDataDome(error.response.headers, error.response.data)
      ) {
        incrementDataDomeRequestBlocked()

        const eventAbortController = new AbortController()

        const timeoutId = setTimeout(() => {
          handleDataDomeCaptchaFailedToRender({
            eventAbortController,
            headers: error.response?.headers,
            url: `${error.response?.config?.baseURL}${error.response?.config?.url}`,
          })
        }, SCRIPT_FAILS_TO_TRIGGER_TIMEOUT)

        window.addEventListener(
          DD_RESPONSE_DISPLAYED_EVENT,
          () => handleDataDomeCaptchaRendered(timeoutId),
          {
            signal: eventAbortController.signal,
            once: true,
          },
        )
      }

      return Promise.reject(error)
    },
  )
}
